/**
 * The app-owned auth panel (FR-2.11): renders the current screen of the in-app auth
 * state machine and wires each form to {@link useAuth}'s actions. Presentational over
 * the passed {@link AuthState} — the single `useAuth` instance lives in {@link SignIn}
 * so the panel and its trigger share one state (a second `useAuth()` here would be a
 * separate machine). Closable so a guest can dismiss it and keep playing (D-43.9).
 */

import type { AuthState } from '../../net/use-auth';
import { recordFunnelStage } from '../../net/rum';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { ResetPasswordForm } from './ResetPasswordForm';
import { SignInForm } from './SignInForm';
import { SignUpForm } from './SignUpForm';

export interface AuthPanelProps {
  auth: AuthState;
}

export function AuthPanel({ auth }: AuthPanelProps): React.JSX.Element {
  const { screen, busy, error, notice } = auth;

  return (
    <div className="auth-panel" data-testid="auth-panel">
      <div className="auth-panel__body">
        {screen === 'signIn' && (
          <SignInForm
            busy={busy}
            error={error}
            notice={notice}
            initialUsername={auth.pendingUsername}
            onSubmit={auth.signIn}
            onForgot={() => auth.goTo('forgot')}
            onSignUp={() => auth.goTo('signUp')}
          />
        )}
        {screen === 'signUp' && (
          <SignUpForm
            busy={busy}
            error={error}
            notice={notice}
            onSubmit={(username, email, password, dateOfBirth) => {
              // Adoption funnel (#148, NFR-6.6): a genuine sign-up attempt. Fires per
              // submit — a submit that fails validation/Cognito yields `signup_started`
              // without the server's `account_registered`, exposing that drop-off.
              recordFunnelStage('signup_started');
              auth.startSignUp(username, email, password, dateOfBirth);
            }}
            onSignIn={() => auth.goTo('signIn')}
          />
        )}
        {screen === 'forgot' && (
          <ForgotPasswordForm
            busy={busy}
            error={error}
            notice={notice}
            onSubmit={auth.forgot}
            onBack={() => auth.goTo('signIn')}
          />
        )}
        {screen === 'reset' && (
          <ResetPasswordForm
            busy={busy}
            error={error}
            notice={notice}
            onSubmit={auth.reset}
          />
        )}
      </div>
      <button
        type="button"
        className="btn btn--ghost"
        data-testid="auth-close"
        onClick={auth.closePanel}
      >
        Continue as a guest
      </button>
    </div>
  );
}
